import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, MoveDown } from "lucide-react";
import { api, imgSrc } from "../lib/api";
import { IMAGES, SERVICES, STATS } from "../lib/data";
import { Eyebrow, FadeUp, LineReveal, SectionHead } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { CtaSection } from "../components/CtaSection";
import { PengumumanPopup } from "../components/PengumumanPopup";

const SPANS = [
  "md:col-span-4",
  "md:col-span-2",
  "md:col-span-2",
  "md:col-span-4",
  "md:col-span-3",
  "md:col-span-3",
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col justify-end overflow-hidden"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Timbangan keadilan"
          className="h-[125%] w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className="gold-radial absolute inset-0" />
        <div className="absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-[#050505] to-transparent" />
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="relative mx-auto w-full max-w-7xl px-6 pb-16 pt-44 lg:px-10"
      >
        <FadeUp delay={0.2}>
          <Eyebrow>Yayasan Bantuan Hukum — Sumatera Timur</Eyebrow>
        </FadeUp>

        <h1 className="mt-8 font-serif text-5xl leading-[1.02] text-[#F5F5F5] sm:text-6xl lg:text-[6.5rem]">
          <LineReveal
            delay={0.3}
            lines={[
              "Keadilan bukan",
              "hak istimewa.",
              <span key="l3" className="italic text-gold">
                Ia hak setiap orang.
              </span>,
            ]}
          />
        </h1>

        <FadeUp delay={0.9}>
          <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-[#C9C9C9] md:text-lg">
            Sejak 2016, YLBH Sumatera Timur mendampingi masyarakat menghadapi
            persoalan hukum — dari ruang sidang hingga meja mediasi — dengan
            integritas dan tanpa memandang kemampuan membayar.
          </p>
        </FadeUp>

        <FadeUp delay={1.1} className="mt-10 flex flex-wrap items-center gap-4">
          <Link
            to="/kontak"
            data-testid="hero-konsultasi-button"
            className="group inline-flex items-center gap-3 bg-gold px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.2em] text-ink transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-light"
          >
            Ajukan Konsultasi
            <ArrowRight
              size={16}
              className="transition-transform duration-300 group-hover:translate-x-1"
            />
          </Link>
          <Link
            to="/layanan"
            data-testid="hero-layanan-button"
            className="inline-flex items-center gap-3 border border-white/25 px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.2em] text-white transition-all duration-300 hover:border-gold hover:text-gold"
          >
            Jelajahi Layanan
          </Link>
        </FadeUp>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.8 }}
        className="relative border-t border-white/10 bg-black/30 backdrop-blur-sm"
      >
        <div className="mx-auto grid w-full max-w-7xl grid-cols-2 divide-x divide-white/10 px-6 lg:grid-cols-4 lg:px-10">
          {STATS.map((s, i) => (
            <div
              key={s.label}
              data-testid={`hero-stat-${i}`}
              className="py-7 pr-4 lg:px-8 lg:first:pl-0"
            >
              <p className="font-serif text-3xl text-gold lg:text-4xl">{s.angka}</p>
              <p className="mt-1 text-[11px] font-medium uppercase tracking-[0.25em] text-[#A3A3A3]">
                {s.label}
              </p>
            </div>
          ))}
        </div>
        <div className="absolute right-6 top-1/2 hidden -translate-y-1/2 items-center gap-2 text-[#737373] lg:flex">
          <MoveDown size={14} className="animate-bounce" />
          <span className="text-[10px] uppercase tracking-[0.3em]">Gulir</span>
        </div>
      </motion.div>
    </section>
  );
};

const AboutPreview = () => (
  <section className="mx-auto w-full max-w-7xl px-6 py-28 lg:px-10 lg:py-36">
    <div className="grid items-center gap-16 lg:grid-cols-2">
      <div>
        <SectionHead
          eyebrow="Tentang Kami"
          titleLines={[
            "Menegakkan hukum,",
            <span key="t" className="italic text-gold">
              menjaga martabat.
            </span>,
          ]}
        />
        <FadeUp delay={0.3}>
          <p className="mt-8 max-w-lg text-base font-light leading-relaxed text-[#A3A3A3] md:text-lg">
            YLBH Sumatera Timur adalah yayasan bantuan hukum yang lahir dari
            keyakinan sederhana: keadilan tidak boleh berhenti di pintu orang
            yang tidak mampu. Sejak 2016 kami mendampingi buruh,
            petani, keluarga, dan pelaku usaha kecil di 8 kabupaten dan kota
            selama satu dekade terakhir.
          </p>
          <Link
            to="/tentang"
            data-testid="about-preview-link"
            className="group mt-8 inline-flex items-center gap-3 text-[13px] font-semibold uppercase tracking-[0.2em] text-gold"
          >
            Baca profil kami
            <ArrowUpRight
              size={16}
              className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
            />
          </Link>
        </FadeUp>
      </div>

      <FadeUp delay={0.2} className="relative">
        <div
          className="absolute -left-4 -top-4 h-full w-full border border-gold/30"
          aria-hidden="true"
        />
        <div className="relative overflow-hidden">
          <motion.img
            src={IMAGES.office}
            alt="Ruang rapat YLBH Sumatera Timur"
            className="aspect-[4/3] w-full object-cover"
            whileHover={{ scale: 1.04 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          />
          <div className="absolute inset-0 bg-black/20" />
        </div>
      </FadeUp>
    </div>
  </section>
);

const ServicesPreview = () => (
  <section className="border-t border-white/10 bg-[#0A0A0A]">
    <div className="mx-auto w-full max-w-7xl px-6 py-28 lg:px-10 lg:py-36">
      <div className="flex flex-wrap items-end justify-between gap-8">
        <SectionHead
          eyebrow="Bidang Praktik"
          titleLines={[
            "Enam bidang hukum,",
            <span key="t" className="italic text-gold">
              satu tujuan.
            </span>,
          ]}
        />
        <FadeUp delay={0.2}>
          <Link
            to="/layanan"
            data-testid="services-preview-link"
            className="group inline-flex items-center gap-3 border border-gold/50 px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.2em] text-gold transition-all duration-300 hover:bg-gold hover:text-ink"
          >
            Semua layanan
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </FadeUp>
      </div>

      <div className="mt-16 grid gap-px border border-white/10 bg-white/10 md:grid-cols-6">
        {SERVICES.map((s, i) => {
          const Icon = s.icon;
          return (
            <FadeUp key={s.id} delay={0.08 * i} className={SPANS[i]}>
              <Link
                to="/layanan"
                data-testid={`service-card-${s.id}`}
                className="group flex h-full flex-col justify-between bg-[#0A0A0A] p-8 transition-colors duration-500 hover:bg-[#101010] lg:p-10"
              >
                <div className="flex items-start justify-between">
                  <Icon
                    size={26}
                    strokeWidth={1.5}
                    className="text-gold/80 transition-colors duration-300 group-hover:text-gold"
                  />
                  <span className="font-serif text-lg italic text-[#4d4d4d]">
                    {s.nomor}
                  </span>
                </div>
                <div className="mt-16">
                  <h3 className="font-serif text-2xl text-[#F5F5F5] transition-colors duration-300 group-hover:text-gold-light lg:text-3xl">
                    {s.judul}
                  </h3>
                  <p className="mt-3 text-sm font-light leading-relaxed text-[#A3A3A3]">
                    {s.ringkasan}
                  </p>
                </div>
              </Link>
            </FadeUp>
          );
        })}
      </div>
    </div>
  </section>
);

const ArticlesPreview = ({ articles }) => {
  if (!articles.length) return null;
  return (
    <section className="border-t border-white/10">
      <div className="mx-auto w-full max-w-7xl px-6 py-28 lg:px-10 lg:py-36">
        <SectionHead
          eyebrow="Wawasan Hukum"
          titleLines={[
            "Artikel &",
            <span key="t" className="italic text-gold">
              kabar terbaru.
            </span>,
          ]}
        />
        <div className="mt-16 grid gap-10 md:grid-cols-3">
          {articles.map((a, i) => (
            <FadeUp key={a.id} delay={0.1 * i}>
              <Link
                to={`/artikel/${a.slug}`}
                data-testid={`home-article-${a.slug}`}
                className="group block"
              >
                <div className="overflow-hidden border border-white/10">
                  <motion.img
                    src={imgSrc(a.gambar)}
                    alt={a.judul}
                    className="aspect-[16/10] w-full object-cover"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                  />
                </div>
                <p className="mt-6 text-[11px] font-medium uppercase tracking-[0.3em] text-gold">
                  {a.kategori} — {a.tanggal}
                </p>
                <h3 className="mt-3 font-serif text-2xl leading-snug text-[#F5F5F5] transition-colors duration-300 group-hover:text-gold-light">
                  {a.judul}
                </h3>
              </Link>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    api
      .get("/articles")
      .then((r) => setArticles(r.data.slice(0, 3)))
      .catch(() => {});
  }, []);

  return (
    <>
      <PengumumanPopup />
      <Hero />
      <Marquee />
      <AboutPreview />
      <ServicesPreview />
      <ArticlesPreview articles={articles} />
      <CtaSection testid="home-cta" />
    </>
  );
}
